import React from 'react'
import { Clock, Grid3x3, Box, Scissors, Layers, Package, Truck, PlusCircle, MinusCircle, Icon } from 'lucide-react';
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import TextWithTooltip from '../../../genericComponents/TextWithTooltip';
import Icons from '../../../assets/icons';
import { formatNumber } from '../../../helper/genericFunction';

function TNADashGraphView(props) {
    const { graphData, selectedStatus, onStatusChange, hideZoomControls = false } = props;

    // Helper to get Icon based on name
    const getIcon = (name) => {
        const n = name?.toLowerCase() || "";
        const iconProps = { size: 16 };
        if (n.includes("fpt") || n.includes("gpt")) return <Icons.FPT {...iconProps} />;
        if (n.includes("pps")) return <Icons.PPS {...iconProps} />;
        if (n.includes("sewing")) return <Icons.Sewing {...iconProps} />;
        if (n.includes("cut")) return <Icons.Cutting {...iconProps} />;
        if (n.includes("pack")) return <Icons.Packaging {...iconProps} />;
        if (n.includes("dispatch") || n.includes("ship")) return <Truck {...iconProps} />;
        if (n.includes("sample")) return <Box {...iconProps} />;
        if (n.includes("qc") || n.includes("approval")) return <Clock {...iconProps} />; // Using Clock or clipboard if available
        if (n.includes("start")) return <Clock {...iconProps} />;
        if (n.includes("fabric")) return <Icons.FabricNew {...iconProps} />;
        if (n.includes("finish")) return <Icons.Finishing {...iconProps} />;
        return <Box {...iconProps} />;
    };

    const getStatusType = (name) => {
        const n = name?.toLowerCase() || "";
        if (n.includes("not started")) return "not-started";
        if (n.includes("ready") || n.includes("done")) return "completed";
        return "in-progress";
    };

    const stages = (graphData || []).map((item, index) => ({
        id: index,
        name: item.status_name,
        icon: item.status_name, // Used for lookup in getIcon
        delayed: item.delayed_count || 0,
        onTrack: item.on_track_count || 0,
        total: item.total_count || 0,
        status: getStatusType(item.status_name),
        isLast: index === (graphData || []).length - 1,
        originalItem: item
    }));

    const WorkflowStage = ({ stage }) => {
        const isSelected = selectedStatus === stage.name;

        // Dynamic styles based on selection
        const cardBorderClass = isSelected ? 'border-[#5D5FEF] shadow-[0px_0px_0px_3px_#5D5FEF33]' : 'border-[#DAE4EE] hover:border-gray-300';
        const dividerClass = isSelected ? 'border-[#5D5FEF]' : 'border-[#DAE4EE]';

        let titleColorClass = 'text-black';
        let iconColorClass = 'text-black';
        let headerBgClass = 'bg-white';

        if (stage.status === 'not-started') {
            headerBgClass = 'bg-[#FF9F43]';
            titleColorClass = 'text-white';
            iconColorClass = 'text-white';
        } else if (stage.status === 'completed') {
        }

        return (
            <div className="relative flex-shrink-0 flex items-center">
                <div
                    onClick={() => onStatusChange && onStatusChange(stage.originalItem)}
                    className={`w-[190px] bg-white rounded-[6px] border ${cardBorderClass} overflow-hidden transition-all duration-200 cursor-pointer`}
                >
                    <div className={`flex items-center gap-3 px-4 py-[8px] border-b ${dividerClass} ${headerBgClass}`}>
                        <div className={`flex ${iconColorClass}`}>
                            {getIcon(stage.name)}
                        </div>
                        <div className={`font-semibold text-[15px] m-0 ${titleColorClass} flex-1 min-w-0 flex`}>
                            <TextWithTooltip 
                                text={stage.status === 'not-started' ? stage.name.toUpperCase() : stage.name}
                                width="100%"
                            />
                        </div>
                    </div>

                    <div className="px-4 py-[4px] flex items-center justify-between bg-white">
                        <div className="flex gap-4 text-xs font-semibold">
                            <div className="flex items-center gap-1.5">
                                <span className="relative flex items-center justify-center h-[8px] w-[8px]">
                                    <span className="absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-30"></span>
                                    <span className="relative inline-flex h-[5px] w-[5px] rounded-full bg-green-500"></span>
                                </span>
                                <div className='group relative'>
                                    <span className="text-gray-500">{formatNumber(stage.onTrack)?.short}</span>
                                    {formatNumber(stage.onTrack)?.full && <span className='generic-tooltip last:left-auto last:right-0 last:after:left-auto last:after:right-[15px] group-hover:opacity-100 group-hover:visible'>{formatNumber(stage.onTrack)?.full}</span>}
                                </div>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <span className="relative flex items-center justify-center h-[8px] w-[8px]">
                                    <span className="absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-30"></span>
                                    <span className="relative inline-flex h-[5px] w-[5px] rounded-full bg-red-500"></span>
                                </span>
                                <div className='group relative'>
                                    <span className="text-gray-500">{formatNumber(stage.delayed)?.short}</span>
                                    {formatNumber(stage.delayed)?.full && <span className='generic-tooltip last:left-auto last:right-0 last:after:left-auto last:after:right-[15px] group-hover:opacity-100 group-hover:visible'>{formatNumber(stage.delayed)?.full}</span>}
                                </div>
                            </div>
                        </div>
                        {!hideZoomControls && <div className='group relative'>
                            <span className="font-bold text-gray-900 text-sm">{formatNumber(stage.total)?.short}</span>
                            {formatNumber(stage.total)?.full && <span className='generic-tooltip last:left-auto last:right-0 last:after:left-auto last:after:right-[15px] group-hover:opacity-100 group-hover:visible'>{formatNumber(stage.total)?.full}</span>}
                        </div>}
                    </div>
                </div>

                {!stage.isLast && (
                    <div className="w-[60px] border-t border-dashed border-slate-300"></div>
                )}
            </div>
        );
    };

    return (
        <div className="h-full w-full py-4 relative">
            <TransformWrapper
                initialScale={0.8}
                minScale={0.2}
                maxScale={3}
                centerOnInit={false}
                limitToBounds={false}
                wheel={{ disabled: false }}
            >
                {({ zoomIn, zoomOut, resetTransform }) => (
                    <>
                        {!hideZoomControls && <div className="absolute top-4 right-4 z-[1] flex items-start gap-6 pointer-events-none">
                            <div className="flex items-center gap-[15px] mt-2 pointer-events-auto">
                                <div className="flex items-center gap-[6px]">
                                    <span className="relative flex items-center justify-center h-[12px] w-[12px]">
                                        <span className="absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-20"></span>
                                        <span className="relative inline-flex h-[8px] w-[8px] rounded-full bg-[#27D298]"></span>
                                    </span>
                                    <span className="text-xs font-medium text-fontBlack">On Track</span>
                                </div>
                                <div className="flex items-center gap-[6px]">
                                    <span className="relative flex items-center justify-center h-[12px] w-[12px]">
                                        <span className="absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-20"></span>
                                        <span className="relative inline-flex h-[8px] w-[8px] rounded-full bg-[#FF4D4F]"></span>
                                    </span>
                                    <span className="text-xs font-medium text-fontBlack">Delayed</span>
                                </div>

                            </div>

                            <div className="flex flex-col gap-3 pointer-events-auto">
                                <button onClick={() => zoomIn()} className="h-[36px] w-[36px] bg-white rounded-lg shadow-sm border border-gray-100 flex items-center justify-center hover:bg-gray-50 transition-colors">
                                    <PlusCircle strokeWidth={1.5} className="text-black" size={24} />
                                </button>
                                <button onClick={() => zoomOut()} className="h-[36px] w-[36px] bg-white rounded-lg shadow-sm border border-gray-100 flex items-center justify-center hover:bg-gray-50 transition-colors">
                                    <MinusCircle strokeWidth={1.5} className="text-black" size={24} />
                                </button>
                            </div>
                        </div>}

                        <TransformComponent wrapperClass="!w-full !h-full" contentClass="!w-full !h-full cursor-grab active:cursor-grabbing">
                            <div className="flex items-center px-20 py-20 min-w-max h-full">
                                {stages.map((stage) => (
                                    <WorkflowStage key={stage.id} stage={stage} />
                                ))}
                            </div>
                        </TransformComponent>
                    </>
                )}
            </TransformWrapper>
        </div>
    )
}

export default TNADashGraphView